import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, Skeleton, Typography } from '@mui/material';
import type { CaseResponseDto } from '@bandido/shared/dtos';
import { useTranslation } from 'react-i18next';

import { CONFIG } from '@frontend/config';
import { useGetCaseById } from '@frontend/features/cases/queries';
import { useCaseTravelers } from '@frontend/features/travelers/queries';
import { addLeadingZeros } from '@frontend/features/common/utils/helpers';
import { defaultLeadingZeros } from '@frontend/features/common/constants';

import {
  BOOKING_CASE_PARAM,
  type BookingPartySuggestion,
  bookingCaseEligible,
  bookingCaseId,
  consumeBookingPartySuggestion,
  currentCaseRoster,
  nextBookingPartySuggestion,
  operatingAgencyId,
  rosterPartySuggestionAvailable,
  ROSTER_PARTY_ROUTES,
} from '../bookingCaseContext';

interface BookingCaseContextValue {
  caseData: CaseResponseDto | null;
  hasCaseContext: boolean;
  partySuggestion: BookingPartySuggestion | null;
  consumePartySuggestion: (revision: number) => void;
  suggestRosterParty: () => void;
  setCase: (caseData: CaseResponseDto | null) => void;
}

const BookingCaseContext = createContext<BookingCaseContextValue>({
  caseData: null,
  hasCaseContext: false,
  partySuggestion: null,
  consumePartySuggestion: () => undefined,
  suggestRosterParty: () => undefined,
  setCase: () => undefined,
});

export const useBookingCaseContext = () => useContext(BookingCaseContext);

export function BookingCaseContextProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [partySuggestion, setPartySuggestion] =
    useState<BookingPartySuggestion | null>(null);
  const caseId = bookingCaseId(searchParams.get(BOOKING_CASE_PARAM));
  const agencyId = operatingAgencyId(CONFIG.AGENCY_ID);
  const caseQuery = useGetCaseById({ id: caseId ?? undefined });
  const caseData = bookingCaseEligible(caseQuery.data, agencyId) ? caseQuery.data : null;
  const roster = useCaseTravelers({
    caseId: caseData?.id ?? 0,
    currentPage: 1,
    pageSize: 1,
    enabled: Boolean(caseData),
  });
  const currentRoster = currentCaseRoster(roster.data, roster.isPreviousData);

  const setCase = useCallback(
    (nextCase: CaseResponseDto | null) => {
      setPartySuggestion(null);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (nextCase) next.set(BOOKING_CASE_PARAM, String(nextCase.id));
          else next.delete(BOOKING_CASE_PARAM);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const suggestRosterParty = useCallback(() => {
    if (!currentRoster) return;
    setPartySuggestion((current) =>
      nextBookingPartySuggestion(current, currentRoster.count));
  }, [currentRoster]);
  const consumePartySuggestion = useCallback((revision: number) => {
    setPartySuggestion((current) =>
      consumeBookingPartySuggestion(current, revision));
  }, []);

  const value = useMemo(
    () => ({
      caseData,
      hasCaseContext: Boolean(caseId),
      partySuggestion,
      consumePartySuggestion,
      suggestRosterParty,
      setCase,
    }),
    [
      caseData,
      caseId,
      partySuggestion,
      consumePartySuggestion,
      setCase,
      suggestRosterParty,
    ],
  );
  const invalid = Boolean(caseId) && !caseQuery.isLoading && !caseData;

  return (
    <BookingCaseContext.Provider value={value}>
      {caseId && caseQuery.isLoading ? (
        <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
      ) : invalid ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setCase(null)}>
              {t('travelServices.caseContext.remove')}
            </Button>
          }
        >
          {t('travelServices.caseContext.invalid')}
        </Alert>
      ) : caseData ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setCase(null)}>
              {t('travelServices.caseContext.remove')}
            </Button>
          }
        >
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="body2">
              {t('travelServices.caseContext.active', {
                number: addLeadingZeros(caseData.id, defaultLeadingZeros),
                subject: caseData.name,
                client: `${caseData.clientFirstName} ${caseData.clientLastName}`.trim(),
              })}
            </Typography>
            {currentRoster && (
              <Chip
                size="small"
                label={t('travelServices.caseContext.travelers', {
                  count: currentRoster.count,
                })}
              />
            )}
            {ROSTER_PARTY_ROUTES.has(location.pathname) &&
              rosterPartySuggestionAvailable(currentRoster?.count ?? 0) && (
              <Button size="small" onClick={suggestRosterParty}>
                {t('travelServices.caseContext.useComposition')}
              </Button>
            )}
            {ROSTER_PARTY_ROUTES.has(location.pathname) &&
              (currentRoster?.count ?? 0) > 0 &&
              !rosterPartySuggestionAvailable(currentRoster?.count ?? 0) && (
              <Typography variant="caption" color="text.secondary">
                {t('travelServices.caseContext.compositionTooLarge', {
                  count: currentRoster?.count,
                })}
              </Typography>
            )}
          </Box>
        </Alert>
      ) : null}
      {children}
    </BookingCaseContext.Provider>
  );
}
